import { isValid as isValidEin } from "ein-validator";
import { stdnum } from "stdnum";
import type { IntlShape } from "react-intl";

import type { SupportedCountry } from "../components/interface";

type CompanyIdValidator = (value?: string) => boolean;

const isValidGermanyCompanyStnr: CompanyIdValidator = (value?: string): boolean => {
  if (!value) {
    return false;
  }
  return stdnum.DE.stnr.validate(value).isValid;
};

const isValidVatNumber = (countryCode: SupportedCountry, value?: string, vatName = "vat"): boolean => {
  if (!value) {
    return false;
  }
  const formattedValue = stdnum[countryCode][vatName].format(value);
  return stdnum[countryCode][vatName].validate(formattedValue).isValid;
};

export const companyIdValidationByCountry: Partial<
  Record<SupportedCountry, { validate: CompanyIdValidator; getMessage: (intl: IntlShape) => string }>
> = {
  US: {
    validate: isValidEin,
    getMessage: (intl) =>
      intl.formatMessage({
        id: "Please enter a valid Employer Identification Number",
        defaultMessage: "Please enter a valid Employer Identification Number",
      }),
  },
  DE: {
    validate: isValidGermanyCompanyStnr,
    getMessage: (intl) =>
      intl.formatMessage({
        id: "Please enter a valid Employer Tax ID",
        defaultMessage: "Please enter a valid Employer Tax ID",
      }),
  },
};

export const companyVatValidationByCountry = (
  country
): { validate: CompanyIdValidator; getMessage: (intl: IntlShape) => string } => {
  const defaultMessage = (intl) =>
    intl.formatMessage({
      id: "Please enter a valid VAT number",
      defaultMessage: "Please enter a valid VAT number",
    });
  const validation = {
    DE: {
      validate: (value) => isValidVatNumber(country, value),
      getMessage: defaultMessage,
    },
    BE: {
      validate: (value) => isValidVatNumber(country, value),
      getMessage: defaultMessage,
    },
    GB: {
      validate: (value) => isValidVatNumber(country, value),
      getMessage: defaultMessage,
    },
    IE: {
      validate: (value) => isValidVatNumber(country, value),
      getMessage: defaultMessage,
    },
    SE: {
      validate: (value) => isValidVatNumber(country, value),
      getMessage: defaultMessage,
    },
    DK: {
      validate: (value) => isValidVatNumber(country, value, "cvr"),
      getMessage: defaultMessage,
    },
    ES: {
      validate: (value) => isValidVatNumber(country, value, "nif"),
      getMessage: defaultMessage,
    },
    FR: {
      validate: (value) => isValidVatNumber(country, value, "tva"),
      getMessage: defaultMessage,
    },
    NL: {
      validate: (value) => isValidVatNumber(country, value, "btw"),
      getMessage: defaultMessage,
    },
    NZ: {
      validate: (value) => isValidVatNumber(country, value, "ird"),
      getMessage: (intl) =>
        intl.formatMessage({
          id: "NZ GST/IRD Number",
          defaultMessage: "NZ GST/IRD Number",
        }),
    },
    AU: {
      validate: (value) => isValidVatNumber(country, value, "abn"),
      getMessage: (intl) =>
        intl.formatMessage({
          id: "Please enter a valid Australian Business Number (ABN)",
          defaultMessage: "Please enter a valid Australian Business Number (ABN)",
        }),
    },
    CA: {
      validate: (value) => isValidVatNumber(country, value, "bn"),
      getMessage: (intl) =>
        intl.formatMessage({
          id: "Please enter a valid Business Number",
          defaultMessage: "Please enter a valid Business Number",
        }),
    },
  };

  return validation[country];
};
